import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import { useFormContext } from "react-hook-form";
import {
  FormActionBar,
  OfficeReviewSection,
} from "../../../Patients/components/clinicalForms/formLayout";
import FormSignatureSections, {
  ElectronicAuthSection,
} from "../../../Patients/components/clinicalForms/FormSignatureSections";
import { parseIsoDateToMDYUS } from "../../../../utils/dateUtils";

/**
 * Patient-chart signature + workflow footer for Daily Log
 * (General Forms and Patient Eval share this).
 */
const DailyLogWorkflowFooter = ({
  isOffice,
  isReadOnly,
  currentStatus,
  employeeName,
  reviewerName,
  saving,
  onSave,
  onSendToOffice,
  onStatusUpdate,
  patientNameLocked = false,
}) => {
  const { watch, setValue, getValues, handleSubmit, formState } =
    useFormContext();
  const applicantSignature = watch("applicantSignature") || "";
  const reviewedBySignature = watch("reviewedBySignature") || "";
  const patientName = watch("patientName") || "";
  const comments = watch("comments") || "";
  const patientSignatureUrl = watch("patientSignatureUrl") || "";
  const patientSignatureImage = watch("patientSignatureImage") || "";
  const clearPatientSignature = watch("clearPatientSignature");
  const displayPatientSig =
    !clearPatientSignature && (patientSignatureImage || patientSignatureUrl);

  const openedAt = useMemo(() => new Date().toISOString(), []);
  const patientStampedAt = displayPatientSig
    ? getValues("updatedAt") || getValues("createdAt") || openedAt
    : openedAt;

  const canSignPatient = !isReadOnly;
  const canSignEmployee = !isOffice && !isReadOnly;
  const canSignAdmin = isOffice && currentStatus !== "completed";
  const showAdmin =
    isOffice || Boolean(reviewedBySignature) || currentStatus === "completed";

  const permissions = {
    canToBeSign: false,
    canSendToOffice: !isOffice,
    canReview: isOffice,
    canEdit: !isReadOnly,
  };

  const stampDate = () => parseIsoDateToMDYUS(new Date().toISOString());

  const applyEmployee = (text, name) => {
    setValue("applicantSignature", text, { shouldDirty: true });
    setValue("applicantName", name || employeeName || "", { shouldDirty: false });
    setValue("applicantSignedAt", stampDate(), { shouldDirty: true });
  };

  const clearEmployee = () => {
    setValue("applicantSignature", "", { shouldDirty: true });
    setValue("applicantSignedAt", "", { shouldDirty: true });
  };

  const applyAdmin = (text, name) => {
    setValue("reviewedBySignature", text, { shouldDirty: true });
    setValue("reviewedBySignedBy", name || reviewerName || "", {
      shouldDirty: true,
    });
    setValue("reviewedBySignedAt", stampDate(), { shouldDirty: true });
  };

  const clearAdmin = () => {
    setValue("reviewedBySignature", "", { shouldDirty: true });
    setValue("reviewedBySignedAt", "", { shouldDirty: true });
  };

  const requireEmployeeSig = () => {
    if (applicantSignature) return true;
    toast.error("Employee signature is required.");
    return false;
  };

  const requireAdminSig = () => {
    if (reviewedBySignature) return true;
    toast.error("Admin signature is required.");
    return false;
  };

  const runValidated = (fn) =>
    handleSubmit(
      (data) => fn(data),
      () => toast.error("Please fix errors before proceeding."),
    )();

  const onBar = (flag) => {
    if (saving) return;
    if (flag === "save") {
      runValidated((data) => onSave(data, "save"));
      return;
    }
    if (flag === "send_to_office") {
      if (!requireEmployeeSig()) return;
      runValidated((data) => onSendToOffice(data, "send_to_office"));
      return;
    }
    if (flag === "to_be_corrected") {
      if (!String(comments || "").trim()) {
        toast.error("Comments are required when requesting changes.");
        return;
      }
      if (!requireAdminSig()) return;
      runValidated((data) =>
        onStatusUpdate("to_be_corrected", { comments: data.comments }),
      );
      return;
    }
    if (flag === "completed") {
      if (!requireAdminSig()) return;
      runValidated((data) =>
        onStatusUpdate("completed", {
          reviewedBySignature: data.reviewedBySignature,
          reviewedBySignedAt: data.reviewedBySignedAt,
          reviewedBySignedBy: data.reviewedBySignedBy,
        }),
      );
    }
  };

  return (
    <>
      <FormSignatureSections
        patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        patientNameValue={patientName}
        onPatientNameChange={
          patientNameLocked || isReadOnly
            ? undefined
            : (next) =>
                setValue("patientName", next, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
        }
        patientSignatureUrl={displayPatientSig || ""}
        patientSignedAt={patientStampedAt}
        onPatientDraw={(dataUrl) => {
          setValue("patientSignatureImage", dataUrl, { shouldDirty: true });
          setValue("clearPatientSignature", false, { shouldDirty: true });
        }}
        onPatientClear={() => {
          setValue("patientSignatureImage", "", { shouldDirty: true });
          setValue("clearPatientSignature", true, { shouldDirty: true });
          setValue("patientSignatureUrl", "", { shouldDirty: true });
        }}
        canSign={canSignPatient}
        patientNameDisabled={patientNameLocked}
        showClinicianSection={false}
      />

      <ElectronicAuthSection
        title="Employee Authentication"
        nameLabel="Signing Employee"
        signerName={employeeName}
        signatureText={applicantSignature}
        canSign={canSignEmployee}
        onSign={applyEmployee}
        onClear={clearEmployee}
        error={formState.errors.applicantSignature?.message || ""}
      />

      {showAdmin ? (
        <ElectronicAuthSection
          title="Office / Admin Authentication"
          nameLabel="Signing Admin"
          signerName={reviewerName}
          signatureText={reviewedBySignature}
          canSign={canSignAdmin && !isReadOnly}
          onSign={applyAdmin}
          onClear={clearAdmin}
          error={formState.errors.reviewedBySignature?.message || ""}
        />
      ) : null}

      {isOffice && currentStatus !== "completed" ? (
        <OfficeReviewSection
          value={comments}
          onChange={(next) =>
            setValue("comments", next, { shouldDirty: true })
          }
        />
      ) : null}

      {currentStatus !== "completed" ? (
        <FormActionBar
          permissions={permissions}
          status={currentStatus}
          canEdit={!isReadOnly}
          saving={saving}
          onSubmit={onBar}
        />
      ) : null}
    </>
  );
};

DailyLogWorkflowFooter.propTypes = {
  isOffice: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  currentStatus: PropTypes.string,
  employeeName: PropTypes.string,
  reviewerName: PropTypes.string,
  saving: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onSendToOffice: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func.isRequired,
  patientNameLocked: PropTypes.bool,
};

export default DailyLogWorkflowFooter;
